import { isDefined } from "@clipboard-health/util-ts";
import { type EmergencyCreditsConfig } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { useEmergencyCreditsConfig } from "@src/appV2/redesign/EmergencyCredits/hooks/useEmergencyCreditsConfig";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

function buildStorageKey(workerId: string): string {
  return `ec-education-seen::${workerId}`;
}

export function hasWorkerSeenEmergencyCreditsEducation(workerId: string): boolean {
  const seenInLocalStorage = localStorage.getItem(buildStorageKey(workerId));
  return isDefined(seenInLocalStorage);
}

export function markWorkerAsSeenEmergencyCreditsEducation(workerId: string): void {
  localStorage.setItem(buildStorageKey(workerId), "true");
}

export type EnabledEmergencyCreditsConfig = Extract<EmergencyCreditsConfig, { enabled: true }>;

interface UseEmergencyCreditsEducationResult {
  shouldShowEmergencyCreditsEducation: boolean;
  isEmergencyCreditsEnabled: boolean;
  hasSeenEmergencyCreditsEducation: boolean;
  markEmergencyCreditsEducationAsSeen: () => void;
  emergencyCreditsConfig: EnabledEmergencyCreditsConfig | undefined;
}

export function useEmergencyCreditsEducation(): UseEmergencyCreditsEducationResult {
  const { userId: workerId } = useDefinedWorker();

  const emergencyCreditsConfig = useEmergencyCreditsConfig();
  const isEmergencyCreditsEnabled = emergencyCreditsConfig.enabled;
  const hasSeenEmergencyCreditsEducation = hasWorkerSeenEmergencyCreditsEducation(workerId);

  const shouldShowEmergencyCreditsEducation =
    isEmergencyCreditsEnabled && !hasSeenEmergencyCreditsEducation;

  return {
    isEmergencyCreditsEnabled,
    hasSeenEmergencyCreditsEducation,
    shouldShowEmergencyCreditsEducation,
    markEmergencyCreditsEducationAsSeen: () => {
      markWorkerAsSeenEmergencyCreditsEducation(workerId);
    },
    emergencyCreditsConfig: emergencyCreditsConfig.enabled ? emergencyCreditsConfig : undefined,
  };
}
